import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { 
  Upload, Download, FileSpreadsheet, Users, Calendar, 
  BarChart3, ClipboardList, RefreshCcw, AlertCircle, 
  CheckCircle2, XCircle, Loader2, FileText, Copy, Trophy, Target, Award, ArrowLeft, DollarSign, UserCog
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DataManagement({ user }) {
  const [activeTab, setActiveTab] = useState("export");
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [terms, setTerms] = useState([]);
  const [sectionTeams, setSectionTeams] = useState([]);
  
  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importType, setImportType] = useState("academy"); // academy or section
  const [importOptions, setImportOptions] = useState({
    update_existing: true,
    auto_enroll: false,
    team_id: "",
    term_id: ""
  });
  const [importResult, setImportResult] = useState(null);
  
  // Export state
  const [exportType, setExportType] = useState("players");
  const [exportFields, setExportFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [exportFilters, setExportFilters] = useState({});
  
  // Copy Term state
  const [copyTermId, setCopyTermId] = useState("");
  const [newTermName, setNewTermName] = useState("");
  const [newStartDate, setNewStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [copySessions, setCopySessions] = useState(true);

  // Bulk Display Name Update state
  const [displayNameFile, setDisplayNameFile] = useState(null);
  const [displayNameResult, setDisplayNameResult] = useState(null);
  const [displayNameLoading, setDisplayNameLoading] = useState(false);

  useEffect(() => {
    fetchTeams();
    fetchTerms();
    fetchSectionTeams();
  }, []);

  useEffect(() => {
    if (exportType) {
      fetchExportFields(exportType);
    }
  }, [exportType]);

  const fetchTeams = async () => {
    try {
      const res = await axios.get(`${API}/academy/teams`, {
        withCredentials: true
      });
      setTeams(res.data || []);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  const fetchTerms = async () => {
    try {
      const res = await axios.get(`${API}/academy/terms`, {
        withCredentials: true
      });
      setTerms(res.data || []);
    } catch (error) {
      console.error("Error fetching terms:", error);
    }
  };

  const fetchSectionTeams = async () => {
    try {
      const res = await axios.get(`${API}/teams`, {
        withCredentials: true
      });
      setSectionTeams(res.data || []);
    } catch (error) {
      console.error("Error fetching section teams:", error);
    }
  };

  const fetchExportFields = async (type) => {
    try {
      const res = await axios.get(`${API}/data/export/fields/${type}`, {
        withCredentials: true
      });
      setExportFields(res.data.fields || []);
      // Select default fields
      const defaults = res.data.fields.filter(f => f.default).map(f => f.name);
      setSelectedFields(defaults);
    } catch (error) {
      console.error("Error fetching export fields:", error);
      setExportFields([]);
    }
  };

  const downloadTemplate = async () => {
    try {
      const endpoint = importType === "academy" 
        ? `${API}/data/import/template/academy-players`
        : `${API}/data/import/template/section-members`;
      
      const res = await axios.get(endpoint, {
        withCredentials: true,
        responseType: "blob"
      });
      
      const filename = importType === "academy" 
        ? "academy_player_import_template.csv"
        : "section_member_import_template.csv";
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Template downloaded!");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error("Please select a file");
      return;
    }

    setLoading(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append("file", importFile);
      
      let url;
      if (importType === "academy") {
        url = `${API}/data/import/academy-players?`;
        url += `update_existing=${importOptions.update_existing}`;
        url += `&auto_enroll=${importOptions.auto_enroll}`;
        if (importOptions.team_id && importOptions.team_id !== "none") url += `&team_id=${importOptions.team_id}`;
        if (importOptions.term_id && importOptions.term_id !== "none") url += `&term_id=${importOptions.term_id}`;
      } else {
        url = `${API}/data/import/section-members?`;
        url += `update_existing=${importOptions.update_existing}`;
        if (importOptions.team_id && importOptions.team_id !== "none") url += `&team_id=${importOptions.team_id}`;
      }

      const res = await axios.post(url, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });

      setImportResult(res.data);
      
      if (res.data.failed === 0) {
        toast.success(`Import complete! ${res.data.created} created, ${res.data.updated} updated`);
      } else {
        toast.warning(`Import complete with ${res.data.failed} errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      toast.error("Please select at least one field to export");
      return;
    }

    setLoading(true);

    try {
      const config = {
        export_type: exportType,
        filters: exportFilters,
        fields: selectedFields,
        format: "xlsx"
      };

      const res = await axios.post(`${API}/data/export`, config, {
        withCredentials: true,
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${exportType}_export.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Export downloaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyTerm = async () => {
    if (!copyTermId || !newTermName) {
      toast.error("Please select a term and enter a name");
      return;
    }

    setLoading(true);

    try {
      let url = `${API}/data/copy-term/${copyTermId}?`;
      url += `new_name=${encodeURIComponent(newTermName)}`;
      url += `&copy_sessions=${copySessions}`;
      url += `&copy_teams=true`;
      if (newStartDate) url += `&new_start_date=${newStartDate}`;
      if (newEndDate) url += `&new_end_date=${newEndDate}`;

      const res = await axios.post(url, {}, { withCredentials: true });
      
      toast.success(`Term copied! ${res.data.sessions_copied} sessions copied.`);
      fetchTerms();
      setCopyTermId("");
      setNewTermName("");
      setNewStartDate("");
      setNewEndDate("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to copy term");
    } finally {
      setLoading(false);
    }
  };

  const toggleField = (fieldName) => {
    setSelectedFields(prev => 
      prev.includes(fieldName)
        ? prev.filter(f => f !== fieldName)
        : [...prev, fieldName]
    );
  };

  const selectAllFields = () => {
    setSelectedFields(exportFields.map(f => f.name));
  };

  const clearAllFields = () => {
    setSelectedFields([]);
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-2">
            <Link to="/admin?tab=tools">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <FileSpreadsheet className="h-7 w-7 text-blue-400" />
              Data Management
            </h1>
          </div>
          <p className="text-slate-400 mt-1 ml-20">Import, export, and manage academy data</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-4 w-full max-w-xl">
            <TabsTrigger value="import" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Import
            </TabsTrigger>
            <TabsTrigger value="export" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Export
            </TabsTrigger>
            <TabsTrigger value="bulk-update" className="flex items-center gap-2">
              <UserCog className="h-4 w-4" />
              Bulk Update
            </TabsTrigger>
            <TabsTrigger value="copy-term" className="flex items-center gap-2">
              <Copy className="h-4 w-4" />
              Copy Term
            </TabsTrigger>
          </TabsList>

          {/* Import Tab */}
          <TabsContent value="import">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Import Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Import Data
                  </CardTitle>
                  <CardDescription>
                    Upload a CSV or Excel file to import data
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Import Type Selection */}
                  <div>
                    <Label>Import Type</Label>
                    <Select value={importType} onValueChange={setImportType}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="academy">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-blue-500" />
                            Academy Players (Youth)
                          </div>
                        </SelectItem>
                        <SelectItem value="section">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-green-500" />
                            Section Members (Adult)
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Button variant="outline" onClick={downloadTemplate} className="mb-4">
                      <FileText className="h-4 w-4 mr-2" />
                      Download {importType === "academy" ? "Academy" : "Section"} Template
                    </Button>
                  </div>

                  <div>
                    <Label>Select File</Label>
                    <Input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      className="mt-1"
                    />
                    {importFile && (
                      <p className="text-sm text-slate-400 mt-1">
                        Selected: {importFile.name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="update_existing"
                        checked={importOptions.update_existing}
                        onCheckedChange={(checked) => 
                          setImportOptions(prev => ({ ...prev, update_existing: !!checked }))
                        }
                      />
                      <Label htmlFor="update_existing">
                        Update existing records (match by {importType === "academy" ? "parent email + child name" : "email"})
                      </Label>
                    </div>

                    {importType === "academy" && (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="auto_enroll"
                          checked={importOptions.auto_enroll}
                          onCheckedChange={(checked) => 
                            setImportOptions(prev => ({ ...prev, auto_enroll: !!checked }))
                          }
                        />
                        <Label htmlFor="auto_enroll">Auto-enroll in sessions</Label>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label>Assign to {importType === "academy" ? "Academy " : ""}Team (Optional)</Label>
                    <Select
                      value={importOptions.team_id || "none"}
                      onValueChange={(value) => setImportOptions(prev => ({ ...prev, team_id: value === "none" ? "" : value }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="No team assignment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No team assignment</SelectItem>
                        {importType === "academy" 
                          ? teams.map(team => (
                              <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                            ))
                          : sectionTeams.map(team => (
                              <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                            ))
                        }
                      </SelectContent>
                    </Select>
                  </div>

                  <Button onClick={handleImport} disabled={!importFile || loading} className="w-full">
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Import {importType === "academy" ? "Academy Players" : "Section Members"}
                  </Button>
                </CardContent>
              </Card>

              {/* Import Results */}
              <Card>
                <CardHeader>
                  <CardTitle>Import Results</CardTitle>
                </CardHeader>
                <CardContent>
                  {importResult ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-500/10 p-4 rounded-lg text-center">
                          <div className="text-2xl font-bold text-blue-400">{importResult.total}</div>
                          <div className="text-sm text-slate-400">Total Rows</div>
                        </div>
                        <div className="bg-emerald-500/10 p-4 rounded-lg text-center">
                          <div className="text-2xl font-bold text-emerald-400">{importResult.created}</div>
                          <div className="text-sm text-slate-400">Created</div>
                        </div>
                        <div className="bg-yellow-500/10 p-4 rounded-lg text-center">
                          <div className="text-2xl font-bold text-yellow-500">{importResult.updated}</div>
                          <div className="text-sm text-slate-400">Updated</div>
                        </div>
                        <div className="bg-red-500/10 p-4 rounded-lg text-center">
                          <div className="text-2xl font-bold text-red-400">{importResult.failed}</div>
                          <div className="text-sm text-slate-400">Failed</div>
                        </div>
                      </div>

                      {importResult.errors && importResult.errors.length > 0 && (
                        <div>
                          <h4 className="font-medium text-red-400 mb-2">Errors:</h4>
                          <div className="bg-red-500/10 rounded-lg p-3 max-h-48 overflow-y-auto">
                            {importResult.errors.map((error, idx) => (
                              <div key={idx} className="text-sm text-red-400 py-1 border-b border-red-500/30 last:border-0">
                                {error}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <FileSpreadsheet className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Import results will appear here</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Export Tab */}
          <TabsContent value="export">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Export Type Selection */}
              <Card className="md:col-span-1">
                <CardHeader>
                  <CardTitle>Export Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={exportType} onValueChange={setExportType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Academy Exports */}
                      <SelectItem value="players">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-blue-500" />
                          Academy Players
                        </div>
                      </SelectItem>
                      <SelectItem value="registrations">
                        <div className="flex items-center gap-2">
                          <ClipboardList className="h-4 w-4 text-blue-500" />
                          Academy Registrations
                        </div>
                      </SelectItem>
                      <SelectItem value="schedules">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-blue-500" />
                          Academy Schedules
                        </div>
                      </SelectItem>
                      <SelectItem value="attendance">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-blue-500" />
                          Academy Attendance
                        </div>
                      </SelectItem>
                      <SelectItem value="player_sessions">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-blue-500" />
                          Player Session Enrollments
                        </div>
                      </SelectItem>
                      <SelectItem value="stats">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="h-4 w-4 text-blue-500" />
                          Academy Stats
                        </div>
                      </SelectItem>
                      <SelectItem value="payments">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-emerald-500" />
                          Payments
                        </div>
                      </SelectItem>
                      {/* Section Exports */}
                      <SelectItem value="section_members">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-green-500" />
                          Section Members
                        </div>
                      </SelectItem>
                      <SelectItem value="section_teams">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-green-500" />
                          Section Teams
                        </div>
                      </SelectItem>
                      <SelectItem value="team_rosters">
                        <div className="flex items-center gap-2">
                          <ClipboardList className="h-4 w-4 text-green-500" />
                          Team Rosters
                        </div>
                      </SelectItem>
                      <SelectItem value="games">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-green-500" />
                          Games/Fixtures
                        </div>
                      </SelectItem>
                      <SelectItem value="match_reports">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-green-500" />
                          Match Reports
                        </div>
                      </SelectItem>
                      <SelectItem value="section_stats">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="h-4 w-4 text-green-500" />
                          Section Stats
                        </div>
                      </SelectItem>
                      {/* Leaderboard Exports */}
                      <SelectItem value="leaderboard">
                        <div className="flex items-center gap-2">
                          <Trophy className="h-4 w-4 text-yellow-500" />
                          📊 Full Leaderboard
                        </div>
                      </SelectItem>
                      <SelectItem value="top_scorers">
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4 text-yellow-500" />
                          ⚽ Top Scorers
                        </div>
                      </SelectItem>
                      <SelectItem value="top_assists">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-yellow-500" />
                          🎯 Top Assists
                        </div>
                      </SelectItem>
                      <SelectItem value="motm_leaders">
                        <div className="flex items-center gap-2">
                          <Award className="h-4 w-4 text-yellow-500" />
                          🏆 MOTM Leaders
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  <div className="mt-4 space-y-3">
                    <Label>Filters</Label>
                    
                    {(exportType === "players" || exportType === "stats" || exportType === "section_members" || exportType === "team_rosters" || exportType === "games" || exportType === "match_reports" || exportType === "section_stats" || exportType === "leaderboard" || exportType === "top_scorers" || exportType === "top_assists" || exportType === "motm_leaders") && (
                      <Select
                        value={exportFilters.team_id || "all"}
                        onValueChange={(value) => setExportFilters(prev => ({ ...prev, team_id: value === "all" ? undefined : value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All Teams" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Teams</SelectItem>
                          {sectionTeams.map(team => (
                            <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {/* Leaderboard source filter */}
                    {(exportType === "leaderboard" || exportType === "top_scorers" || exportType === "top_assists" || exportType === "motm_leaders") && (
                      <Select
                        value={exportFilters.source || "all"}
                        onValueChange={(value) => setExportFilters(prev => ({ ...prev, source: value === "all" ? undefined : value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All Sources" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All (Academy + Section)</SelectItem>
                          <SelectItem value="academy">Academy Only</SelectItem>
                          <SelectItem value="section">Section Only</SelectItem>
                        </SelectContent>
                      </Select>
                    )}

                    {/* Leaderboard sort by filter */}
                    {exportType === "leaderboard" && (
                      <Select
                        value={exportFilters.sort_by || "goals"}
                        onValueChange={(value) => setExportFilters(prev => ({ ...prev, sort_by: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sort By" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="goals">Sort by Goals</SelectItem>
                          <SelectItem value="assists">Sort by Assists</SelectItem>
                          <SelectItem value="motm">Sort by MOTM</SelectItem>
                          <SelectItem value="games_played">Sort by Games Played</SelectItem>
                          <SelectItem value="contributions">Sort by G+A (Contributions)</SelectItem>
                        </SelectContent>
                      </Select>
                    )}

                    {exportType === "registrations" && (
                      <Select
                        value={exportFilters.status || "all"}
                        onValueChange={(value) => setExportFilters(prev => ({ ...prev, status: value === "all" ? undefined : value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All Statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    )}

                    {(exportType === "schedules" || exportType === "registrations" || exportType === "player_sessions" || exportType === "payments") && (
                      <Select
                        value={exportFilters.term_id || "all"}
                        onValueChange={(value) => setExportFilters(prev => ({ ...prev, term_id: value === "all" ? undefined : value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All Terms" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Terms</SelectItem>
                          {terms.map(term => (
                            <SelectItem key={term.id} value={term.id}>{term.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {exportType === "payments" && (
                      <Select
                        value={exportFilters.status || "all"}
                        onValueChange={(value) => setExportFilters(prev => ({ ...prev, status: value === "all" ? undefined : value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All Statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="verified">Verified</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="disputed">Disputed</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Field Selection */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Select Fields</span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={selectAllFields}>
                        Select All
                      </Button>
                      <Button variant="outline" size="sm" onClick={clearAllFields}>
                        Clear All
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {exportFields.map(field => (
                      <div key={field.name} className="flex items-center space-x-2">
                        <Checkbox
                          id={field.name}
                          checked={selectedFields.includes(field.name)}
                          onCheckedChange={() => toggleField(field.name)}
                        />
                        <Label htmlFor={field.name} className="text-sm cursor-pointer">
                          {field.label}
                        </Label>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-400">
                        {selectedFields.length} fields selected
                      </div>
                      <Button onClick={handleExport} disabled={loading || selectedFields.length === 0}>
                        {loading ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4 mr-2" />
                        )}
                        Export to Excel
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Bulk Update Tab */}
          <TabsContent value="bulk-update">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Bulk Display Name Update */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCog className="h-5 w-5 text-purple-400" />
                    Bulk Update Display Names
                  </CardTitle>
                  <CardDescription>
                    Upload a CSV file to update display names for multiple members at once.
                    The full legal name will be preserved for charge sheets.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Template Download */}
                  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                    <h4 className="text-sm font-semibold text-white mb-2">CSV Format Required</h4>
                    <p className="text-xs text-slate-400 mb-3">
                      Your CSV file should have two columns:
                    </p>
                    <div className="bg-slate-900 p-3 rounded font-mono text-xs text-slate-300 mb-3">
                      <div className="text-yellow-400">membership_number,display_name</div>
                      <div>P1234,Johnny Tan</div>
                      <div>ACJ001,Mike L.</div>
                      <div>C5678HFM,Sarah W.</div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const template = "membership_number,display_name\nP1234,Johnny Tan\nACJ001,Mike L.\nC5678HFM,Sarah W.";
                        const blob = new Blob([template], { type: "text/csv" });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.setAttribute("download", "display_name_template.csv");
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success("Template downloaded!");
                      }}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Download Template
                    </Button>
                  </div>

                  {/* File Upload */}
                  <div>
                    <Label>Upload CSV File</Label>
                    <Input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => {
                        setDisplayNameFile(e.target.files[0]);
                        setDisplayNameResult(null);
                      }}
                      className="mt-1"
                    />
                  </div>

                  {displayNameFile && (
                    <Alert className="bg-slate-800/50 border-slate-700">
                      <FileSpreadsheet className="h-4 w-4" />
                      <AlertDescription className="text-slate-300">
                        Selected: <span className="font-medium">{displayNameFile.name}</span>
                      </AlertDescription>
                    </Alert>
                  )}

                  <Button
                    onClick={async () => {
                      if (!displayNameFile) {
                        toast.error("Please select a file");
                        return;
                      }
                      
                      setDisplayNameLoading(true);
                      setDisplayNameResult(null);
                      
                      try {
                        const formData = new FormData();
                        formData.append("file", displayNameFile);
                        
                        const res = await axios.post(
                          `${API}/data/bulk-update-display-names`,
                          formData,
                          {
                            withCredentials: true,
                            headers: { "Content-Type": "multipart/form-data" }
                          }
                        );
                        
                        setDisplayNameResult(res.data);
                        
                        if (res.data.failed === 0) {
                          toast.success(`Success! Updated ${res.data.updated} display names`);
                        } else {
                          toast.warning(`Completed with ${res.data.failed} errors`);
                        }
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Update failed");
                      } finally {
                        setDisplayNameLoading(false);
                      }
                    }}
                    disabled={!displayNameFile || displayNameLoading}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {displayNameLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Update Display Names
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Results Panel */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardList className="h-5 w-5" />
                    Update Results
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {displayNameResult ? (
                    <div className="space-y-4">
                      {/* Summary Stats */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                          <div className="text-2xl font-bold text-green-400">{displayNameResult.updated}</div>
                          <div className="text-xs text-slate-400">Updated</div>
                        </div>
                        <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-center">
                          <div className="text-2xl font-bold text-yellow-400">{displayNameResult.skipped || 0}</div>
                          <div className="text-xs text-slate-400">Skipped</div>
                        </div>
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
                          <div className="text-2xl font-bold text-red-400">{displayNameResult.failed}</div>
                          <div className="text-xs text-slate-400">Failed</div>
                        </div>
                      </div>

                      {/* Updated List */}
                      {displayNameResult.updated_records && displayNameResult.updated_records.length > 0 && (
                        <Accordion type="single" collapsible>
                          <AccordionItem value="updated">
                            <AccordionTrigger className="text-sm">
                              <span className="flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4 text-green-400" />
                                Successfully Updated ({displayNameResult.updated_records.length})
                              </span>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="max-h-40 overflow-y-auto space-y-1">
                                {displayNameResult.updated_records.map((record, idx) => (
                                  <div key={idx} className="text-xs p-2 bg-slate-800/50 rounded flex justify-between">
                                    <span className="text-slate-400">{record.membership_number}</span>
                                    <span className="text-green-400">→ {record.display_name}</span>
                                  </div>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      )}

                      {/* Errors List */}
                      {displayNameResult.errors && displayNameResult.errors.length > 0 && (
                        <Accordion type="single" collapsible>
                          <AccordionItem value="errors">
                            <AccordionTrigger className="text-sm">
                              <span className="flex items-center gap-2">
                                <XCircle className="h-4 w-4 text-red-400" />
                                Errors ({displayNameResult.errors.length})
                              </span>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="max-h-40 overflow-y-auto space-y-1">
                                {displayNameResult.errors.map((error, idx) => (
                                  <div key={idx} className="text-xs p-2 bg-red-500/10 border border-red-500/20 rounded">
                                    <span className="text-slate-300">{error.membership_number}:</span>
                                    <span className="text-red-400 ml-2">{error.error}</span>
                                  </div>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <UserCog className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>Upload a file to see results</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Copy Term Tab */}
          <TabsContent value="copy-term">
            <Card className="max-w-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Copy className="h-5 w-5" />
                  Copy Term
                </CardTitle>
                <CardDescription>
                  Create a new term by copying an existing one. Sessions will be duplicated without enrolled players.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Source Term</Label>
                  <Select value={copyTermId} onValueChange={setCopyTermId}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select term to copy" />
                    </SelectTrigger>
                    <SelectContent>
                      {terms.map(term => (
                        <SelectItem key={term.id} value={term.id}>
                          {term.name} ({term.weeks} weeks)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>New Term Name</Label>
                  <Input
                    value={newTermName}
                    onChange={(e) => setNewTermName(e.target.value)}
                    placeholder="e.g., Term 2 2026"
                    className="mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>New Start Date (Optional)</Label>
                    <Input
                      type="date"
                      value={newStartDate}
                      onChange={(e) => setNewStartDate(e.target.value)}
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-400 mt-1">Leave empty to keep same dates</p>
                  </div>
                  <div>
                    <Label>New End Date (Optional)</Label>
                    <Input
                      type="date"
                      value={newEndDate}
                      onChange={(e) => setNewEndDate(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="copy_sessions"
                    checked={copySessions}
                    onCheckedChange={(checked) => setCopySessions(!!checked)}
                  />
                  <Label htmlFor="copy_sessions">Copy session templates</Label>
                </div>

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    The new term will be created as inactive. Activate it manually after reviewing.
                  </AlertDescription>
                </Alert>

                <Button 
                  onClick={handleCopyTerm} 
                  disabled={!copyTermId || !newTermName || loading}
                  className="w-full"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  Copy Term
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
